import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, User, Bot, Mail, Building2, Phone, FileText, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadFormType, setLeadFormType] = useState('kurzbewertung');
  const [leadData, setLeadData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    acceptPrivacy: false
  });
  const messagesEndRef = useRef(null);

  const quickReplies = [
    { text: 'Leistungsbereiche', query: 'Welche Leistungen bieten Sie an?' },
    { text: 'Arbeitsweise', query: 'Wie läuft die Zusammenarbeit ab?' },
    { text: 'Kurzbewertung', query: 'Ich möchte eine Kurzbewertung anfragen' },
    { text: 'Kontakt', query: 'Wie kann ich Sie kontaktieren?' }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Welcome message
      const welcomeMessage = {
        role: 'assistant',
        content: 'Guten Tag! Ich bin der digitale Assistent von Smart Post Solution. Wie kann ich Ihnen bei Fragen zu Versandlogistik und Briefkonsolidierung helfen?',
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen]);

  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: messageText,
        session_id: sessionId
      });

      if (!sessionId) {
        setSessionId(response.data.session_id);
      }

      const assistantMessage = {
        role: 'assistant',
        content: response.data.message,
        timestamp: new Date(response.data.timestamp)
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Check if message contains lead generation keywords
      if (messageText.toLowerCase().includes('kurzbewertung') || 
          messageText.toLowerCase().includes('erstgespräch') ||
          messageText.toLowerCase().includes('kontakt')) {
        // Show quick action for lead form
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Entschuldigung, es gab einen Fehler. Bitte versuchen Sie es erneut oder nutzen Sie unser Kontaktformular am Seitenende.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickReply = (query) => {
    sendMessage(query);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    
    if (!leadData.acceptPrivacy) {
      alert('Bitte stimmen Sie der Datenschutzerklärung zu.');
      return;
    }
    
    setIsLoading(true);

    try {
      await axios.post(`${API}/lead`, {
        session_id: sessionId || 'direct',
        name: leadData.name,
        email: leadData.email,
        company: leadData.company,
        phone: leadData.phone,
        message: leadData.message,
        request_type: leadFormType
      });

      const successMessage = {
        role: 'assistant',
        content: `Vielen Dank für Ihr Interesse! Wir haben Ihre Anfrage für ${leadFormType === 'kurzbewertung' ? 'eine Kurzbewertung' : 'ein Erstgespräch'} erhalten und melden uns schnellstmöglich bei Ihnen.`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, successMessage]);
      setShowLeadForm(false);
      setLeadData({ name: '', email: '', company: '', phone: '', message: '', acceptPrivacy: false });
    } catch (error) {
      console.error('Lead capture error:', error);
      alert('Es gab einen Fehler. Bitte nutzen Sie unser Kontaktformular am Seitenende.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 right-6 w-16 h-16 bg-slate-800 hover:bg-slate-700 text-white rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 z-50 flex items-center justify-center group shimmer-button"
          aria-label="Chat öffnen"
        >
          <MessageCircle size={28} className="group-hover:rotate-12 transition-transform relative z-10" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse z-10"></span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl z-50 flex flex-col border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Bot size={22} />
              </div>
              <div>
                <h3 className="font-semibold">Smart Post Solution</h3>
                <p className="text-xs text-slate-200">Wir helfen Ihnen gerne</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 p-2 rounded-full transition-colors"
              aria-label="Chat schließen"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex gap-3 ${
                  msg.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot size={16} className="text-slate-600" />
                  </div>
                )}
                <div className="max-w-[75%] flex flex-col gap-2">
                  <div
                    className={`p-3 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-slate-800 text-white rounded-br-none'
                        : 'bg-slate-100 text-slate-900 rounded-bl-none'
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-line">
                      {(msg.content || '').replace('[[WHITEPAPER]]', '').trim()}
                    </p>
                  </div>
                  {msg.role === 'assistant' && (msg.content || '').includes('[[WHITEPAPER]]') && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsOpen(false);
                        setTimeout(() => {
                          const el = document.getElementById('whitepaper');
                          if (el) {
                            const offset = 80;
                            const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
                            window.scrollTo({ top: y, behavior: 'smooth' });
                          }
                        }, 250);
                      }}
                      className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white hover:border-slate-900 hover:shadow-md transition-all duration-200 px-3 py-2.5 text-left"
                      data-testid="chatbot-whitepaper-action"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: 'rgba(255, 162, 102, 0.15)' }}
                        >
                          <FileText size={15} style={{ color: '#F08C4A' }} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-slate-900 leading-tight">
                            5 Hebel zur Portooptimierung
                          </div>
                          <div className="text-[11px] text-slate-500 leading-tight mt-0.5">
                            Senior-Consultant-Checkliste · 7 Seiten
                          </div>
                        </div>
                      </div>
                      <Download
                        size={14}
                        className="text-slate-400 group-hover:text-slate-900 transition-colors flex-shrink-0"
                      />
                    </button>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center flex-shrink-0">
                    <User size={16} className="text-white" />
                  </div>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="flex gap-3 justify-start">
                <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot size={16} className="text-slate-600" />
                </div>
                <div className="bg-slate-100 text-slate-900 p-3 rounded-2xl rounded-bl-none">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          {messages.length === 1 && !isLoading && (
            <div className="px-4 pb-3">
              <div className="flex flex-wrap gap-2">
                {quickReplies.map((reply, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickReply(reply.query)}
                    className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full transition-colors border border-slate-200"
                  >
                    {reply.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Lead Form */}
          {showLeadForm && (
            <div className="px-4 pb-3 max-h-[400px] overflow-y-auto">
              <form onSubmit={handleLeadSubmit} className="space-y-3">
                <Input
                  placeholder="Name"
                  value={leadData.name}
                  onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                  className="text-sm"
                />
                <Input
                  type="email"
                  placeholder="E-Mail *"
                  value={leadData.email}
                  onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                  required
                  className="text-sm"
                />
                <Input
                  placeholder="Unternehmen"
                  value={leadData.company}
                  onChange={(e) => setLeadData({ ...leadData, company: e.target.value })}
                  className="text-sm"
                />
                <Input
                  type="tel"
                  placeholder="Telefon"
                  value={leadData.phone}
                  onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })}
                  className="text-sm"
                />
                <Textarea
                  placeholder="Ihre Nachricht (optional)"
                  value={leadData.message}
                  onChange={(e) => setLeadData({ ...leadData, message: e.target.value })}
                  className="text-sm min-h-[60px]"
                />
                
                {/* Datenschutz Checkbox */}
                <div className="flex items-start gap-2 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    id="privacy-checkbox"
                    checked={leadData.acceptPrivacy}
                    onChange={(e) => setLeadData({ ...leadData, acceptPrivacy: e.target.checked })}
                    className="mt-0.5 cursor-pointer"
                    required
                  />
                  <label htmlFor="privacy-checkbox" className="cursor-pointer leading-relaxed">
                    Ich habe die{' '}
                    <a
                      href="/datenschutz"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-800 underline hover:text-slate-600 font-medium"
                      data-testid="chatbot-datenschutz-link"
                    >
                      Datenschutzerklärung
                    </a>
                    {' '}gelesen und stimme der Verarbeitung meiner Daten zu. *
                  </label>
                </div>

                <div className="flex gap-2">
                  <Button type="submit" disabled={isLoading || !leadData.acceptPrivacy} className="flex-1 text-sm bg-slate-800 hover:bg-slate-700">
                    Senden
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowLeadForm(false)}
                    className="text-sm"
                  >
                    Abbrechen
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Input */}
          {!showLeadForm && (
            <div className="p-4 border-t border-slate-200 bg-white">
              <div
                className="mb-3 p-2.5 rounded-md bg-amber-50 border border-amber-200 text-[11px] leading-relaxed text-amber-900"
                data-testid="chat-privacy-notice"
              >
                Bitte geben Sie keine personenbezogenen Daten oder Gesundheitsdaten ein. Ihre Eingaben werden zur Beantwortung an OpenAI (USA) übermittelt und 90 Tage gespeichert. Mehr in der{' '}
                <a
                  href="/datenschutz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium hover:text-amber-700"
                  data-testid="chat-notice-datenschutz-link"
                >
                  Datenschutzerklärung
                </a>.
              </div>
              <div className="flex gap-2">
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage(inputMessage)}
                  placeholder="Ihre Nachricht..."
                  disabled={isLoading}
                  className="flex-1"
                />
                <Button
                  onClick={() => sendMessage(inputMessage)}
                  disabled={isLoading || !inputMessage.trim()}
                  className="bg-slate-800 hover:bg-slate-700"
                >
                  <Send size={18} />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Chatbot;